import { ChevronRight } from 'lucide-react'
import { Badge, Card, cx, toneForPriority } from '@/components/ui/primitives'
import { statusTone, type TaskStatus } from '@/features/roadmap/types'
import { CLEARED_ITEM_IDS, type UrlState } from '@/lib/urlState'
import type { ActionItem } from './actionItems'

type Navigate = (next: Partial<UrlState>) => void

/** How many rows a card shows before it hands over to the tab that holds the rest. */
const LIMIT = 8
const UP_NEXT = 4

/* Leaving the home page by a footer link opens the tab itself, so any id left in the
   address is cleared rather than read as a drawer to open. */
const TAB = (tab: string): Partial<UrlState> => ({
  tab, ...CLEARED_ITEM_IDS,
})

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

function daysFrom(iso: string, today: Date) {
  const target = new Date(`${iso}T00:00:00`).getTime()
  return Math.round((target - new Date(today.toDateString()).getTime()) / 86_400_000)
}

/** Lateness in days, because "3 days past due" is the fact; the date is how you work it out. */
function dueLabel(item: ActionItem, today: Date): string {
  if (item.due === null) return /^[—-]?$/.test(item.dueText.trim()) ? '' : item.dueText
  const days = daysFrom(item.due, today)
  if (days < 0) return `${plural(-days, 'day')} past due`
  if (days === 0) return 'Due today'
  return `Due ${new Date(`${item.due}T00:00:00`)
    .toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
}

function ItemBadge({ item }: { item: ActionItem }) {
  if (item.kind === 'task') {
    return <Badge tone={statusTone(item.status as TaskStatus)}>{item.status}</Badge>
  }
  return <Badge tone={toneForPriority(item.priority ?? '')}>{item.priority}</Badge>
}

function ActionList({
  items, today, onNavigate,
}: {
  items: ActionItem[]
  today: Date
  onNavigate: Navigate
}) {
  return (
    <ul className="-mx-2 divide-y divide-mc-line">
      {items.map((item) => {
        const due = dueLabel(item, today)
        return (
          <li key={item.key}>
            <button
              type="button"
              onClick={() => onNavigate(item.target)}
              className="group flex w-full items-start gap-3 rounded px-2 py-2.5 text-left hover:bg-mc-surface-2 focus-visible:ring-2 focus-visible:ring-mc-primary focus-visible:outline-none"
            >
              <span
                className={cx(
                  'w-24 shrink-0 pt-0.5 text-xs font-bold break-words text-mc-primary',
                  item.kind !== 'owner' && 'font-mono',
                )}
              >
                {item.ref}
              </span>
              <span className="min-w-0 flex-1 text-sm text-mc-body">{item.title}</span>
              <span className="flex shrink-0 flex-col items-end gap-1">
                <ItemBadge item={item} />
                {due && (
                  <span className={cx('text-xs', item.overdue ? 'font-bold text-mc-critical' : 'text-mc-muted')}>
                    {due}
                  </span>
                )}
              </span>
              <ChevronRight
                size={16}
                className="mt-0.5 shrink-0 text-mc-muted group-hover:text-mc-primary"
                aria-hidden
              />
            </button>
          </li>
        )
      })}
    </ul>
  )
}

function FooterLink({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs font-medium text-mc-primary-light underline-offset-2 hover:underline"
    >
      {children}
    </button>
  )
}

/**
 * Everything open that is waiting on Missional Marketing, most urgent first. Each row
 * opens the item it names, so the card is somewhere to act from rather than a summary to
 * go and find things from.
 */
export function WaitingOnYouCard({
  items, today, onNavigate,
}: {
  items: ActionItem[]
  today: Date
  onNavigate: Navigate
}) {
  const overdue = items.filter((i) => i.overdue).length
  const count = (kind: ActionItem['kind']) => items.filter((i) => i.kind === kind).length
  const owners = count('owner')
  const decisions = count('decision')
  const tasks = count('task')

  const subtitle = items.length === 0
    ? 'Nothing outstanding'
    : `${plural(items.length, 'open item')} waiting on Missional Marketing`
      + (overdue > 0 ? ` · ${overdue} past due` : '')

  return (
    <Card title="What we need from you" subtitle={subtitle}>
      {items.length === 0 ? (
        <p className="text-sm text-mc-muted">Nothing is waiting on you right now.</p>
      ) : (
        <>
          <ActionList items={items.slice(0, LIMIT)} today={today} onNavigate={onNavigate} />
          {items.length > LIMIT && (
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-mc-line pt-3">
              <span className="text-xs text-mc-muted">
                Showing the {LIMIT} most urgent of {items.length}.
              </span>
              {owners > 0 && (
                <FooterLink onClick={() => onNavigate(TAB('owners'))}>
                  {`All ${plural(owners, 'owner')} →`}
                </FooterLink>
              )}
              {decisions > 0 && (
                <FooterLink onClick={() => onNavigate(TAB('decisions'))}>
                  {`All ${plural(decisions, 'decision')} →`}
                </FooterLink>
              )}
              {tasks > 0 && (
                <FooterLink onClick={() => onNavigate(TAB('roadmap'))}>
                  {`All ${plural(tasks, 'task')} on the Roadmap →`}
                </FooterLink>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  )
}

/**
 * Milestone's work under way, and what starts after it. The same rows as the other card,
 * so the two read as one board split by whose move it is.
 */
export function InFlightCard({
  active, next, today, onNavigate,
}: {
  active: ActionItem[]
  next: ActionItem[]
  today: Date
  onNavigate: Navigate
}) {
  const blocked = active.filter((i) => i.status === 'Blocked').length
  const subtitle = active.length === 0
    ? 'Nothing under way right now'
    : `${plural(active.length, 'task')} under way` + (blocked > 0 ? ` · ${blocked} blocked` : '')

  return (
    <Card title="In flight now" subtitle={subtitle}>
      {active.length > 0 && (
        <ActionList items={active.slice(0, LIMIT)} today={today} onNavigate={onNavigate} />
      )}
      {next.length > 0 && (
        <div className={active.length > 0 ? 'mt-4' : undefined}>
          <h3 className="mb-1 text-xs font-bold tracking-wide text-mc-muted uppercase">Up next</h3>
          <ActionList items={next.slice(0, UP_NEXT)} today={today} onNavigate={onNavigate} />
        </div>
      )}
      {active.length === 0 && next.length === 0 && (
        <p className="text-sm text-mc-muted">Nothing is scheduled yet.</p>
      )}
      <div className="mt-3 border-t border-mc-line pt-3">
        <FooterLink onClick={() => onNavigate(TAB('roadmap'))}>Open the Roadmap →</FooterLink>
      </div>
    </Card>
  )
}
